import React, { useEffect, useRef } from 'react'
import { Box, Fade, Stack } from '@mui/material'

import { NARROW_COLUMN_WIDTH } from 'config'
import { MegaphoneIcon } from 'lucide-react'

import { AnnouncementCard, EmptyState } from 'components/display'
import { QueryErrorState } from 'components/feedback'
import { StickyFormHeader } from 'components/navigation'
import { DEFERRED_LOADING_FADE_MS, useDeferredLoading } from 'hooks'
import { useAnnouncementFeed, useMarkAnnouncementSeen } from 'hooks/api'

import { WhatsNewPageSkeleton } from './WhatsNewPageSkeleton'

/**
 * `/whats-new` — the in-app changelog archive (VRG-221).
 *
 * THE ARCHIVE HALF of the four rules. The modal shows at most one announcement, once;
 * this page carries everything else, permanently. That split is what lets dismissal be
 * permanent without losing anything: there is always somewhere to go back and read.
 *
 * Replaces the sidebar Help row's old link to the marketing site
 * (`https://vera.inc/whats-new`), which had two problems this fixes. It navigated the
 * user OUT of the product to read about the product, and it could not know who was
 * reading — so it could not gate by permission, could not hide the backlog that
 * predates a new user, and above all could not say "you asked for this".
 *
 * Renders the SAME `AnnouncementCard` as the modal (§36) — see that component for why
 * that is load-bearing rather than tidy.
 *
 * Three states, all of them explicit (never a spinner, never a bare blank):
 *   - loading  → a footprint-matched skeleton (§19)
 *   - error    → `QueryErrorState`, which distinguishes "cannot load" from
 *                "not allowed". An empty state on a 403 would tell a user there was
 *                nothing new when there was plenty they simply could not see.
 *   - empty    → an honest empty state
 */
export const WhatsNewPage = () => {
  const { data, isLoading, error, refetch } = useAnnouncementFeed()
  const markSeen = useMarkAnnouncementSeen()

  const announcements = data?.announcements ?? []

  /**
   * Whether the skeleton may be on screen. See `useDeferredLoading` — a feed that answers in
   * ~30ms otherwise mounts a skeleton for two frames, which is the "flash" this removes. The
   * skeleton's footprint already matches the loaded feed, so there was never any movement to
   * fix; the appearance itself was the artifact.
   */
  const showSkeleton = useDeferredLoading(isLoading)

  /**
   * READING THE ARCHIVE MARKS ITS ROWS SEEN.
   *
   * Without this the badge is unclearable in practice. The modal is the only other
   * path that marks anything, it shows exactly one announcement (`unseenLatest`,
   * "at most one, by construction"), and a `sessionStorage` guard limits it to one
   * interruption per tab. So a reader with four unseen could clear at most one per
   * session — and reading all four here cleared none. "4 new" would sit on the Help
   * row for days, in front of someone who had read everything.
   *
   * Marking on view is the standard reading of what the badge claims: it counts
   * announcements you have not been shown, and the archive shows them. The weaker
   * alternative — an explicit "Mark all as read" button — was considered and
   * rejected as REDUNDANT rather than wrong: once viewing marks them, the button has
   * nothing left to act on. Only one of the two can exist.
   *
   * Fires once per id. `requestedRef` guards the window between the mutation and the
   * feed invalidation, where the rows are still `seen: false` in cache and a
   * re-render would otherwise re-post them.
   */
  const requestedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const a of announcements) {
      if (a.seen || requestedRef.current.has(a.id)) continue
      requestedRef.current.add(a.id)
      markSeen.mutate(a.id)
    }
    // Deliberately keyed on `announcements` alone. `markSeen` is a mutation object
    // whose identity changes as its state does, so depending on it would re-run this
    // on every settle — the loop `requestedRef` exists to stop. (No eslint-disable:
    // `react-hooks/exhaustive-deps` is not configured in this project, and disabling
    // a rule that does not exist is itself a lint error.)
  }, [announcements])

  return (
    /*
      The CAP WRAPS THE WHOLE PAGE, title included — not just the feed.

      Capping only the content left the title at the page's left edge while the cards
      sat centred hundreds of pixels away, reading as two unrelated things. The
      settings pages already establish the convention and it is the opposite of what
      I first shipped: measured on /settings/personal/appearance, the title and its
      cards share a left edge exactly (both 714px, content 1100 wide). A centred
      column owns its heading.
    */
    <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
      {/*
        STICKY, with the component's hairline edge.

        The archive is a long feed, and its header is the only thing on the page that
        says which page you are on. The same component the create/edit forms use, so
        the fiddly parts — locating the real scroll parent (the app scrolls inside
        <main>, not the window), masking the scrollport's 24px padding band so cards
        are not visible sliding through it — are solved once rather than per page.

        This passed `edge="fade"` on the argument that a rule reads as one more card
        border among a column of them. The fade was removed because it could not be
        seen: page background and card background differ by 6-7 per channel, so a gradient
        one to the other dissolves near-black into near-black. See the note at the top
        of `StickyFormHeader` for the measurement and why height could not fix it.
      */}
      <StickyFormHeader
        title="What's new"
        // The count is meaningful here — it is how much history exists for THIS
        // reader, after the audience gate and the signup cutoff.
        total={announcements.length}
      />

      {showSkeleton ? (
        <WhatsNewPageSkeleton />
      ) : isLoading ? null : error ? (
        <QueryErrorState error={error} subject="announcements" onRetry={() => refetch()} />
      ) : announcements.length === 0 ? (
        <EmptyState
          icon={<MegaphoneIcon strokeWidth={1} />}
          title="Nothing new yet"
          // Says WHY it might be empty rather than just that it is. A brand-new
          // account legitimately sees nothing here, and without this line that reads
          // as a broken page.
          text="Product updates will appear here as they ship. Anything released before you joined is not listed."
        />
      ) : (
        // No cap here — the page wrapper above owns it, so the title and the cards
        // cannot disagree about where the column starts.
        //
        // Opacity only. The skeleton and this feed occupy the same footprint, so the fade is
        // polish on a stable layout; a transform or a height animation would reintroduce the
        // shift the footprint pass removed.
        <Fade in timeout={DEFERRED_LOADING_FADE_MS}>
          <Stack spacing={4}>
            {announcements.map((announcement, index) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
                variant="page"
                /*
                  THE TOUR RINGS THE FIRST CARD, NOT THE FEED (§56).

                  The anchor used to sit on the `Stack` above, whose height is
                  `n × card` and grows every time we ship: 976 × 1749.5 against an
                  800px viewport, which `canSpotlight` refuses to ring because two of
                  its four edges are only "where the screen stops". So the step
                  degraded to a flat dim on every screen, and would have done forever
                  — the target gets bigger, never smaller.

                  `index === 0` rather than every card: `findAnchor` is a
                  `querySelector`, so N cards carrying the same id would make the ring
                  land on whichever came first in the DOM by accident rather than by
                  decision. The newest entry is the one the step is about.

                  This does NOT introduce a new way for the anchor to vanish. It is
                  inside the same `announcements.length > 0` branch the old anchor was
                  in, so the empty archive has exactly the behaviour it had yesterday:
                  no anchor, tour not "on this page", still launchable from the tour
                  library as two centered cards.
                */
                dataTour={index === 0 ? 'whats-new-entry' : undefined}
              />
            ))}
          </Stack>
        </Fade>
      )}
    </Box>
  )
}
